import { QueryClientProvider } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';
import { BrowserRouter, Route, Routes, useParams } from 'react-router';
import { ApiKlientProvider, lagApiKlient, lagQueryClient } from './api/klient.ts';
import { lagSesjonsspoerring } from './api/sesjon.ts';
import { Sesjonsvakt } from './api/Sesjonsvakt.tsx';
import { AuthProvider, useAuth } from './auth/AuthProvider.tsx';
import type { AdminAuth } from './auth/typer.ts';
import type { PanelKonfig } from './konfig.ts';
import { Innlogging } from './sider/Innlogging.tsx';
import { MinInnlogging } from './sider/MinInnlogging.tsx';
import { Innboks, Innstillinger, KjenteFeil, Organisasjon, Panelbruker, Panelbrukere, Programmer, Saker, NyPanelbruker } from './sider/Sider.tsx';
import { Tofaktor } from './sider/Tofaktor.tsx';
import { Skall } from './skall/Skall.tsx';
import { SmalSkjermProvider, useSmalSkjerm } from './skall/useSmalSkjerm.ts';
import { UtkastProvider } from './innstillinger/utkast.tsx';
import { KonfigProvider } from './konfigKontekst.tsx';
import { HModus } from './hypoteser/HModus.tsx';
import { EnSideRute } from './hypoteser/EnSide.tsx';
import { Forside } from './skall/Forside.tsx';
import { Kjoering } from './hypoteser/Kjoering.tsx';
import { SkjermenIRommet } from './hypoteser/SkjermenIRommet.tsx';

export interface AppProps {
  konfig: PanelKonfig;
  auth: AdminAuth;
  /** Injiserbar `fetch` — testene gir en fake. */
  fetch?: typeof fetch;
}

/**
 * Panelet: én QueryClient, én API-klient som leser tokenet fra porten per kall, og
 * en port (Skjerm) som velger skjermbilde etter innloggingstilstanden. Rutene finnes
 * bare bak en sesjon som oppfyller leietakerens tofaktorkrav — det er skallet som
 * velger, ikke URL-en.
 *
 * `sesjon` er luken `GET /v1/admin/session`, bygget HER fordi dette er det ene stedet
 * `src/api/**` og `src/auth/**` møtes: adapteren eier den genererte klienten (vakt 2),
 * `AuthProvider` kjenner bare funksjonstypen. Kallet ligger utenfor `ApiKlientProvider`
 * med vilje — provideren bygges inne i tilstanden luken skal avgjøre.
 */
export function App({ konfig, auth, fetch: hent }: AppProps) {
  const queryClient = useMemo(() => lagQueryClient(), []);
  const sesjon = useMemo(() => lagSesjonsspoerring(konfig.apiUrl, hent), [konfig.apiUrl, hent]);
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider auth={auth} sesjon={sesjon}>
        {/* Konfigen ligger over HELE treet, ikke bare bak låsen: tofaktorskjermene
            rendres utenfor skallet og trenger flagget for enhetsfaktoren
            (`auth/enhet.ts`). Alternativet — å tre flagget ned som prop til Tofaktor —
            ville gitt to steder å hente den samme verdien fra, og ett av dem å glemme. */}
        <KonfigProvider verdi={konfig}>
          <Skjerm konfig={konfig} hent={hent} />
        </KonfigProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

function Skjerm({ konfig, hent }: { konfig: PanelKonfig; hent?: typeof fetch }) {
  const auth = useAuth();
  const klient = useMemo(() => lagApiKlient(konfig.apiUrl, auth.hentToken, hent), [konfig.apiUrl, auth.hentToken, hent]);
  /**
   * Breakpointen måles HER, ikke bare i skallet (DD-51, tofaktor-konsept v0.1 P4):
   * tofaktorskjermene rendres utenfor skallet, og uten en måling over dem måtte
   * «under 900 px» blitt en `@media` med tallet skrevet inn. Skallet måler fortsatt
   * selv — samme spørring, samme svar — og setter klassen på `.panel`.
   */
  const smal = useSmalSkjerm();

  switch (auth.tilstand.navn) {
    case 'laster':
      return (
        <main className="panel-enkelt" id="innhold">
          <p role="status" className="panel-tekst">
            Laster …
          </p>
        </main>
      );
    case 'utlogget':
    case 'kode-sendt':
      return <Innlogging googleLogin={konfig.googleLogin} />;
    case 'tofaktor-oppsett':
    case 'tofaktor-utfordring':
      return (
        <SmalSkjermProvider value={smal}>
          {/* `ukjentKrav`: luken svarte ikke, så skjermen står fordi vi ikke VET — ikke
              fordi leietakeren krever det. Skjermen sier det selv (K8). */}
          <Tofaktor modus={auth.tilstand.navn === 'tofaktor-oppsett' ? 'oppsett' : 'utfordring'} ukjentKrav={auth.tilstand.ukjentKrav} />
        </SmalSkjermProvider>
      );
    case 'innlogget':
      return (
        <ApiKlientProvider value={klient}>
          <Sesjonsvakt>
            <Ruter />
          </Sesjonsvakt>
        </ApiKlientProvider>
      );
  }
}

function Ruter({ children }: { children?: ReactNode }) {
  return (
    <BrowserRouter>
      <Routes>
        {/* «Én side» (H3.5, P8): utskriftssiden står UTENFOR skallet — ingen meny, bare «Lukk» og
            «Skriv ut». Den åpnes i ny fane fra detaljen; sesjonslåsen står fortsatt foran (K8). */}
        <Route path="/h/registeret/:nr/en-side" element={<EnSideRute />} />
        {/* H3.2: kjøringen og skjermen i rommet står også UTENFOR skallet (DD-47 presisert 2026-10-29):
            et eget vindu uten sidemeny. Skjermen i rommet viser bare den lukkede MeetingScreen. */}
        <Route path="/h/moter/:id/skjerm" element={<SkjermRute />} />
        <Route path="/h/moter/:id/kjor/:nr?" element={<KjoeringRute />} />
        <Route path="*" element={<Skallet>{children}</Skallet>} />
      </Routes>
    </BrowserRouter>
  );
}

function SkjermRute() {
  const { id } = useParams();
  return <SkjermenIRommet key={id} moeteId={id ?? ''} />;
}

function KjoeringRute() {
  const { id, nr } = useParams();
  const tall = Number((nr ?? '').replace(/^h-/i, ''));
  return <Kjoering key={id} moeteId={id ?? ''} nr={Number.isInteger(tall) && tall > 0 ? tall : null} />;
}

function Skallet({ children }: { children?: ReactNode }) {
  return (
    <>
      {/* Utkastet i Innstillinger ligger OVER ruten, så det overlever at man går til
          en annen side og tilbake (F32). Skallet står inne i provideren fordi
          sidemenyens merke leser antallet. */}
      <UtkastProvider>
      <Skall>
        <Routes>
          {/* Forsiden følger tilgangen: Innboks for dem som svarer på innspill, Mine hypoteser
              for en bidragsyter uten rommet Innspill (ux-design.md 2.2, H2.2). */}
          <Route path="/" element={<Forside />} />
          <Route path="/innboks" element={<Innboks />} />
          <Route path="/saker" element={<Saker />} />
          {/* /programmer/nytt?fra=H-142 er skjemaet åpnet fra hypotesen (H3.4) — samme side og samme
              instans, så kvitteringen og det valgte programmet står når adressen går tilbake. */}
          <Route path="/programmer/*" element={<Programmer />} />
          <Route path="/kjente-feil" element={<KjenteFeil />} />
          <Route path="/innstillinger" element={<Innstillinger />} />
          {/* Utenfor rommene (DD-01). Ruten finnes for alle roller — seksjonene forklarer
              selv hva eier kan og de andre ikke (K18); lenken finnes bare for eier. */}
          <Route path="/organisasjon" element={<Organisasjon />} />
          {/* Organisasjons undersider (M4.8, DD-68 presisert 2026-09-25): lista, «Legg til
              person» og personen. Eierens alene, som Organisasjon; et medlem på direkte rute får
              samme «Denne siden er for eier». `ny` står før `:id`, så den aldri leses som en id. */}
          <Route path="/organisasjon/brukere" element={<Panelbrukere />} />
          <Route path="/organisasjon/brukere/ny" element={<NyPanelbruker />} />
          <Route path="/organisasjon/brukere/:id" element={<Panelbruker />} />
          {/* Også utenfor rommene, og utenfor Organisasjon: dette er BRUKERENS egen konto,
              ikke leietakerens. Alle roller har den, og den nås fra «⋯» (DD-48). Ruten ligger
              her inne, så en utlogget eller ubekreftet sesjon aldri får den — låsen står
              foran hele treet, ikke foran hver rute (K8). */}
          <Route path="/min-innlogging" element={<MinInnlogging />} />
          {/* Innsendermodus (H2.2): rommet Hypoteser, den ansattes flate. Én rute med stjerne,
              så skjemaet er samme instans på /h/ny og /h/utkast/{id}/{ledd}. */}
          <Route path="/h/*" element={<HModus />} />
          <Route path="*" element={<Forside />} />
        </Routes>
        {children}
      </Skall>
      </UtkastProvider>
    </>
  );
}
